import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';

import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';

import AddressLookup2 from 'components/RMSAddressLookup/AddressLookup2';
import RMSCheckbox from 'components/RMSCheckbox';
import Dialog from 'components/RMSDialog';
import { Row, RowInner, ColCard } from 'utils/formStyles';

import { handleError } from 'reducers/ErrorReducer';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';
import { notify } from 'reducers/NotifierReducer';
import { closePrimaryAddress } from 'reducers/PersonDialogsReducer';

import {
  savePersonAddress,
  getPeopleAddresses,
  updatePersonAddress,
  savePlaceAddress,
  updatePlaceAddress,
  getPlaceAddressData,
} from 'reducers/SearchReducer';

const useStyles = makeStyles((theme) => ({
  closeButton: { color: theme.button.color },
  addressWidget: {
    width: 550,
    display: 'inline-block',
  },
}));

const ENTITY_CONFIG = {
  person: {
    label: 'Person',
    save: savePersonAddress,
    update: updatePersonAddress,
    getExisting: ({ ptsPersonID, ptsAddressID }) =>
      getPeopleAddresses({
        ptsPersonID,
        ptsAddressID,
        IsDeleted: true,
      }),
    hasExisting: (res) => res.total > 0,
    buildSaveData: (data, ptsAddressID, IsPrimary) => ({
      ptsPersonID: data.ptsPersonID,
      ptsAddressID: data?.ptsAddressID || ptsAddressID,
      IsPrimary,
    }),
    restorePayload: (saveData) => ({
      ...saveData,
      IsDeleted: false,
    }),
  },

  place: {
    label: 'Place',
    save: savePlaceAddress,
    update: updatePlaceAddress,
    getExisting: ({ ptsPlaceID, ptsAddressID }) => getPlaceAddressData(ptsPlaceID, ptsAddressID),
    hasExisting: (res) => res.length > 0,
    buildSaveData: (data, ptsAddressID, IsPrimary) => ({
      ptsPlaceID: data.ptsPlaceID,
      ptsAddressID: data?.ptsAddressID || ptsAddressID,
      IsPrimary,
    }),
    restorePayload: (saveData) => saveData,
  },
};

function AddEditPrimaryAddressDialog(props) {
  const classes = useStyles();

  const { data = {}, onAfterSave } = props;
  const entityType = data?.entityType || 'person';
  const config = useMemo(() => ENTITY_CONFIG[entityType] || ENTITY_CONFIG.person, [entityType]);
  const [address, setAddress] = useState(null);
  const [IsPrimary, setIsPrimary] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isEditMode = Boolean(data?.ptsAddressID);
  const dialogTitle = isEditMode ? 'Edit Address' : 'Add Address';
  const canSave = Boolean(data?.ptsAddressID || address?.ptsAddressID);

  useEffect(() => {
    setIsPrimary(data?.IsPrimary === true);
    setAddress(null);
  }, [data]);

  const handleClose = () => {
    props.closePrimaryAddress();
  };
  const handleRemove = async () => {
    props.notifyDataUpdate({
      type: 'Delete Address',
      data: {},
    });

    handleClose();
  };
  const saveAddress = async ({ ptsAddressID, IsPrimary }) => {
    const saveData = config.buildSaveData(data, ptsAddressID, IsPrimary);
    if (isEditMode) {
      await config.update(saveData);
      props.notify('Address updated', 'success');
      props.notifyDataUpdate({
        type: 'Address',
        data: {},
      });
      return;
    }

    const existing = await config.getExisting(saveData);
    if (config.hasExisting(existing)) {
      await config.update(config.restorePayload(saveData));
    } else {
      await config.save(saveData);
    }
    props.notify('Address created', 'success');
    props.notifyDataUpdate({
      type: 'Address',
      data: {},
    });
  };

  const handleSave = async () => {
    const payload = {
      ptsAddressID: data?.ptsAddressID || address?.ptsAddressID,
      IsPrimary,
    };
    setIsSaving(true);
    props.showSpinner();
    try {
      await saveAddress(payload);
      onAfterSave?.();
      handleClose();
    } catch (err) {
      props.handleError(err);
    } finally {
      setIsSaving(false);
      props.hideSpinner();
    }
  };

  const renderAddress = () => (
    <AddressLookup2
      className={classes.addressWidget}
      ptsPlaces={true}
      ptsAddresses={true}
      googleAddresses={false}
      onAddressValueSet={(val) => setAddress(val)}
      onReset={() => setAddress(null)}
      label={isEditMode ? 'Address' : 'Search Address'}
      disabled={isEditMode}
      ptsAddressID={data?.ptsAddressID || null}
      compact
      hideAddButton={isEditMode}
      showDropDownIcon={!isEditMode}
    />
  );

  const renderIsPrimary = () => (
    <RMSCheckbox
      selectedCheckbox={IsPrimary}
      handleFormChange={() => setIsPrimary((prev) => !prev)}
      label="Is Primary"
    />
  );

  const renderActions = () => (
    <>
      {isEditMode && (
        <Button color="primary" variant="contained" onClick={handleRemove} disabled={isSaving}>
          <DeleteIcon />
          Remove Address From {config.label} Record
        </Button>
      )}

      <Button
        color="primary"
        variant="contained"
        autoFocus
        disabled={!canSave || isSaving}
        onClick={handleSave}>
        <SaveIcon /> Save
      </Button>

      <Button color="primary" onClick={handleClose} className={classes.closeButton}>
        <CloseIcon /> Close
      </Button>
    </>
  );

  return (
    <Dialog toolbar onClose={handleClose} title={dialogTitle} actions={renderActions()}>
      <Row>
        <ColCard minWidth={500}>
          <RowInner>{renderAddress()}</RowInner>
          <RowInner>{renderIsPrimary()}</RowInner>
        </ColCard>
      </Row>
    </Dialog>
  );
}

export default connect(null, {
  handleError,
  showSpinner,
  hideSpinner,
  notifyDataUpdate,
  notify,
  closePrimaryAddress,
})(AddEditPrimaryAddressDialog);
